import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Download, Upload, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable CSV bulk-import dialog.
 * Props:
 *  - open, onOpenChange
 *  - endpoint: e.g. "/teams/bulk-csv"
 *  - title: dialog heading
 *  - templateHeader: string, first line of the sample CSV
 *  - templateExample: string, one or more example rows
 *  - templateFilename: "teams_template.csv" | "players_template.csv"
 *  - columnHelp: array of {name, desc} strings for the docs section
 *  - onDone: callback after a successful import (reload the list)
 */
export default function BulkCsvDialog({
  open, onOpenChange, endpoint, title,
  templateHeader, templateExample, templateFilename,
  columnHelp = [], onDone,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setResult(null); setBusy(false); };

  const download = () => {
    const csv = `${templateHeader}\n${templateExample}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = templateFilename; a.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) { toast.error("Selecione um arquivo CSV"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (r.data.created_count > 0) toast.success(`${r.data.created_count} registro(s) criado(s)`);
      if (r.data.error_count > 0) toast.warning(`${r.data.error_count} linha(s) com erro`);
      if (onDone) onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no import");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}
    >
      <DialogContent className="bg-navy-deep border-navy-border text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-xl md:text-2xl">{title}</DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="bg-navy border border-navy-border rounded-sm p-3">
              <div className="font-heading uppercase tracking-widest text-orange text-xs mb-2">Colunas esperadas</div>
              <ul className="space-y-1 text-xs md:text-sm text-ink-secondary">
                {columnHelp.map((c) => (
                  <li key={c.name}><span className="font-mono text-white">{c.name}</span> — {c.desc}</li>
                ))}
              </ul>
              <Button
                type="button"
                variant="outline"
                onClick={download}
                data-testid="bulk-csv-download-template"
                className="mt-3 bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest text-xs rounded-sm h-8"
              >
                <Download size={12} className="mr-1" /> Baixar modelo CSV
              </Button>
            </div>

            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Arquivo CSV</Label>
              <input
                data-testid="bulk-csv-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-2 block w-full text-sm text-white file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-orange file:text-white file:font-heading file:uppercase file:tracking-widest file:text-xs hover:file:bg-orange-hover"
              />
              {file && <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-2">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-court-green">
              <CheckCircle2 size={18} />
              <span className="font-heading uppercase tracking-widest">{result.created_count} criado(s)</span>
            </div>
            {result.error_count > 0 && (
              <div>
                <div className="flex items-center gap-2 text-court-red mb-2">
                  <AlertTriangle size={18} />
                  <span className="font-heading uppercase tracking-widest">{result.error_count} erro(s)</span>
                </div>
                <div className="bg-navy border border-navy-border rounded-sm p-2 max-h-56 overflow-y-auto text-xs font-mono" data-testid="bulk-csv-error-list">
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-ink-secondary">
                      <span className="text-orange">linha {e.row}:</span> {e.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <Button
              data-testid="bulk-csv-submit-btn"
              type="button"
              disabled={busy || !file}
              onClick={submit}
              className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm"
            >
              <Upload size={14} className="mr-2" /> {busy ? "Enviando…" : "Importar"}
            </Button>
          ) : (
            <Button
              data-testid="bulk-csv-close-btn"
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm"
            >
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
